import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Popconfirm,
  Typography,
  Select,
  Switch,
  InputNumber,
  Row,
  Col,
  Card,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DragOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { keyboardAPI, botAPI } from '../services/api';

const { Title, Text } = Typography;

// 键盘按钮类型定义
interface KeyboardButton {
  id: number;
  bot_id: number | null;
  button_text: string;
  button_type: string;
  button_value: string;
  row_index: number;
  col_index: number;
  status: number;
  description: string | null;
  create_time: string;
}

interface BotConfig {
  id: number;
  bot_name: string;
  bot_username: string;
}

const KeyboardButtons: React.FC = () => {
  const [buttons, setButtons] = useState<KeyboardButton[]>([]);
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [visualEditorVisible, setVisualEditorVisible] = useState(false);
  const [editingButton, setEditingButton] = useState<KeyboardButton | null>(null);
  const [selectedBotId, setSelectedBotId] = useState<number | null>(null);
  const [filters, setFilters] = useState<{
    botId?: number | null;
    status?: number;
  }>({});
  const [form] = Form.useForm();

  // 获取机器人列表
  const fetchBots = async () => {
    try {
      const response = await botAPI.getAll();
      setBots(response.data);
    } catch (error: any) {
      message.error('获取机器人列表失败');
    }
  };

  // 获取按钮列表
  const fetchButtons = async () => {
    setLoading(true);
    try {
      const response = await keyboardAPI.getAll(filters);
      setButtons(response.data);
    } catch (error: any) {
      message.error('获取按钮列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  useEffect(() => {
    fetchButtons();
  }, [filters]);

  // 打开新建/编辑弹窗
  const openModal = (button?: KeyboardButton) => {
    if (button) {
      setEditingButton(button);
      form.setFieldsValue({
        botId: button.bot_id,
        buttonText: button.button_text,
        buttonType: button.button_type,
        buttonValue: button.button_value,
        rowIndex: button.row_index,
        colIndex: button.col_index,
        status: button.status === 1,
        description: button.description,
      });
    } else {
      setEditingButton(null);
      form.resetFields();
      form.setFieldsValue({
        buttonType: 'text',
        rowIndex: 0,
        colIndex: 0,
        status: true,
      });
    }
    setModalVisible(true);
  };

  // 提交表单
  const handleSubmit = async (values: any) => {
    try {
      const data = {
        botId: values.botId || null,
        buttonText: values.buttonText,
        buttonType: values.buttonType,
        buttonValue: values.buttonValue,
        rowIndex: values.rowIndex,
        colIndex: values.colIndex,
        status: values.status ? 1 : 0,
        description: values.description,
      };

      if (editingButton) {
        await keyboardAPI.update(editingButton.id, data);
        message.success('按钮已更新');
      } else {
        await keyboardAPI.create(data);
        message.success('按钮已创建');
      }

      setModalVisible(false);
      form.resetFields();
      fetchButtons();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  // 删除按钮
  const handleDelete = async (id: number) => {
    try {
      await keyboardAPI.delete(id);
      message.success('按钮已删除');
      fetchButtons();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  // 打开可视化编辑器
  const openVisualEditor = () => {
    if (selectedBotId === undefined) {
      message.warning('请先选择一个机器人');
      return;
    }
    setVisualEditorVisible(true);
  };

  // 保存可视化布局
  const handleSaveLayout = async (newButtons: any[]) => {
    try {
      await keyboardAPI.replaceAll(selectedBotId, newButtons);
      message.success('键盘布局已保存');
      setVisualEditorVisible(false);
      fetchButtons();
    } catch (error: any) {
      message.error(error.response?.data?.error || '保存失败');
    }
  };

  // 生成键盘预览（按行列排列）
  const renderKeyboardPreview = () => {
    const filteredButtons = buttons.filter(
      (btn) =>
        btn.bot_id === selectedBotId &&
        btn.status === 1
    );

    if (filteredButtons.length === 0) {
      return <Alert message="暂无启用的按钮" type="info" />;
    }

    // 按行索引分组
    const rows: Record<number, KeyboardButton[]> = {};
    filteredButtons.forEach((btn) => {
      if (!rows[btn.row_index]) {
        rows[btn.row_index] = [];
      }
      rows[btn.row_index].push(btn);
    });

    // 每行按列索引排序
    Object.keys(rows).forEach((rowIndex) => {
      rows[Number(rowIndex)].sort((a, b) => a.col_index - b.col_index);
    });

    return (
      <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 16 }}>
        {Object.keys(rows)
          .sort((a, b) => Number(a) - Number(b))
          .map((rowIndex) => (
            <Row key={rowIndex} gutter={8} style={{ marginBottom: 8 }}>
              {rows[Number(rowIndex)].map((btn) => (
                <Col key={btn.id}>
                  <Button
                    type={btn.button_type === 'url' ? 'link' : 'default'}
                    style={{ minWidth: 100 }}
                  >
                    {btn.button_text}
                  </Button>
                </Col>
              ))}
            </Row>
          ))}
      </div>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '机器人',
      dataIndex: 'bot_id',
      width: 120,
      render: (botId: number | null) => {
        if (botId === null) return <Tag color="purple">全局</Tag>;
        const bot = bots.find((b) => b.id === botId);
        return bot ? bot.bot_name : `ID:${botId}`;
      },
    },
    {
      title: '按钮文字',
      dataIndex: 'button_text',
      width: 150,
    },
    {
      title: '类型',
      dataIndex: 'button_type',
      width: 80,
      render: (type: string) => (
        <Tag color={type === 'text' ? 'blue' : 'green'}>
          {type === 'text' ? '文本' : '链接'}
        </Tag>
      ),
    },
    {
      title: '值',
      dataIndex: 'button_value',
      width: 200,
      ellipsis: true,
    },
    {
      title: '行索引',
      dataIndex: 'row_index',
      width: 80,
    },
    {
      title: '列索引',
      dataIndex: 'col_index',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status: number) =>
        status === 1 ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            启用
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            禁用
          </Tag>
        ),
    },
    {
      title: '操作',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: KeyboardButton) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定删除此按钮吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 操作栏 */}
      <div style={{ marginBottom: 16 }}>
        <Title level={3}>键盘按钮设置</Title>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Select
              placeholder="按机器人筛选"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => {
                setFilters({ ...filters, botId: value });
                setSelectedBotId(value ?? null);
              }}
            >
              <Select.Option value={null}>全局按钮</Select.Option>
              {bots.map((bot) => (
                <Select.Option key={bot.id} value={bot.id}>
                  {bot.bot_name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Select
              placeholder="按状态筛选"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => setFilters({ ...filters, status: value })}
            >
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Col>
        </Row>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchButtons}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            新建按钮
          </Button>
          <Button
            type="dashed"
            icon={<AppstoreOutlined />}
            onClick={openVisualEditor}
            disabled={selectedBotId === undefined}
          >
            可视化编辑器（暂未实现）
          </Button>
        </Space>
      </div>

      <Row gutter={16}>
        {/* 左侧：按钮列表 */}
        <Col span={16}>
          <Card title="按钮列表" size="small">
            <Table
              columns={columns}
              dataSource={buttons}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1000 }}
            />
          </Card>
        </Col>

        {/* 右侧：键盘预览 */}
        <Col span={8}>
          <Card title="键盘预览" size="small">
            {selectedBotId !== undefined ? (
              renderKeyboardPreview()
            ) : (
              <Alert message="请在左侧选择机器人以查看键盘预览" type="info" />
            )}
          </Card>
        </Col>
      </Row>

      {/* 新建/编辑弹窗 */}
      <Modal
        title={editingButton ? '编辑按钮' : '新建按钮'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="机器人" name="botId">
            <Select placeholder="选择机器人（留空=全局）" allowClear>
              {bots.map((bot) => (
                <Select.Option key={bot.id} value={bot.id}>
                  {bot.bot_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="按钮文字"
            name="buttonText"
            rules={[{ required: true, message: '请输入按钮显示文字' }]}
          >
            <Input placeholder="例如：📕使用说明" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="按钮类型"
                name="buttonType"
                rules={[{ required: true, message: '请选择按钮类型' }]}
              >
                <Select>
                  <Select.Option value="text">文本（发送命令）</Select.Option>
                  <Select.Option value="url">链接（打开URL）</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.buttonType !== curr.buttonType}
              >
                {({ getFieldValue }) => (
                  <Form.Item
                    label="按钮值"
                    name="buttonValue"
                    rules={[{ required: true, message: '请输入按钮值' }]}
                  >
                    <Input
                      placeholder={
                        getFieldValue('buttonType') === 'text'
                          ? '例如：/start'
                          : '例如：https://example.com'
                      }
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="布局说明"
            description="行索引和列索引决定按钮在键盘中的位置，从0开始。同一行的按钮会并排显示。"
            type="info"
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="行索引（第几行）"
                name="rowIndex"
                rules={[{ required: true, message: '请输入行索引' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="列索引（第几列）"
                name="colIndex"
                rules={[{ required: true, message: '请输入列索引' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="启用" name="status" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item label="描述" name="description">
            <Input placeholder="按钮说明（可选）" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingButton ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 可视化编辑器弹窗（占位，实际功能需要拖拽库实现） */}
      <Modal
        title="可视化键盘编辑器"
        open={visualEditorVisible}
        onCancel={() => setVisualEditorVisible(false)}
        footer={null}
        width={800}
      >
        <Alert
          message="功能提示"
          description="可视化拖拽编辑器需要集成拖拽库（如 react-dnd 或 dnd-kit）。此处仅为占位，完整实现需要额外开发。"
          type="warning"
          showIcon
          icon={<DragOutlined />}
        />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            建议实现功能：
            <ul>
              <li>拖拽按钮调整位置</li>
              <li>实时预览键盘布局</li>
              <li>一键保存所有按钮配置</li>
            </ul>
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default KeyboardButtons;
